import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  CircularProgress,
  Alert,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import FilterListIcon from '@mui/icons-material/FilterList'
import {
  fetchPaymentHistory,
  downloadInvoice,
  PaymentHistoryItem,
} from '@/api/billingAPI'

interface PaymentHistoryProps {
  orgUUID: string
  token: string
  syncStatus?: string | null // billing_sync_status from dashboard
}

const getTypeChipColor = (type: string) => {
  switch (type) {
    case 'Payment':
      return { bg: '#E3F2FD', color: '#1976D2' }
    case 'Charge':
      return { bg: '#FFF3E0', color: '#E65100' }
    case 'CreditNote':
      return { bg: '#E8F5E9', color: '#388E3C' }
    default:
      return { bg: '#F5F5F5', color: '#757575' }
  }
}

const getPlatformChipColor = (platform: string) => {
  switch (platform) {
    case 'Stripe':
      return { bg: '#EDE7F6', color: '#5E35B1' }
    case 'Invoice':
      return { bg: '#E0F7FA', color: '#00838F' }
    default:
      return { bg: '#F5F5F5', color: '#757575' }
  }
}

export default function PaymentHistory({
  orgUUID,
  token,
  syncStatus,
}: PaymentHistoryProps) {
  const [payments, setPayments] = useState<PaymentHistoryItem[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState<number | null>(null)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [platformFilter, setPlatformFilter] = useState<string>('')
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [showFilters, setShowFilters] = useState(false)

  const loadPayments = useCallback(async () => {
    if (!orgUUID || !token) return

    setLoading(true)
    setError(null)

    try {
      const response = await fetchPaymentHistory({
        orgUUID,
        token,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        platform: platformFilter || null,
        startDate: startDate || null,
        endDate: endDate || null,
      })
      setPayments(response.items)
      setTotal(response.total)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load payment history',
      )
    } finally {
      setLoading(false)
    }
  }, [orgUUID, token, page, rowsPerPage, platformFilter, startDate, endDate])

  useEffect(() => {
    loadPayments()
  }, [loadPayments])

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handlePlatformFilterChange = (event: SelectChangeEvent<string>) => {
    setPlatformFilter(event.target.value)
    setPage(0)
  }

  const handleStartDateChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setStartDate(event.target.value)
    setPage(0)
  }

  const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(event.target.value)
    setPage(0)
  }

  const handleDownload = async (paymentId: number) => {
    setDownloading(paymentId)
    try {
      await downloadInvoice(paymentId, orgUUID, token)
    } catch (err) {
      console.error('Failed to download invoice')
      setError(
        err instanceof Error ? err.message : 'Failed to download invoice',
      )
    } finally {
      setDownloading(null)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-NZ', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  const formatAmount = (amount: number) => {
    const isNegative = amount < 0
    const formatted = Math.abs(amount).toFixed(2)
    return isNegative ? `-$${formatted}` : `$${formatted}`
  }

  if (loading && payments.length === 0) {
    return (
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Payment History
          </Typography>

          <IconButton onClick={() => setShowFilters(!showFilters)} size="small">
            <FilterListIcon />
          </IconButton>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        {showFilters && (
          <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Platform</InputLabel>
              <Select
                value={platformFilter}
                label="Platform"
                onChange={handlePlatformFilterChange}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Stripe">Stripe</MenuItem>
                <MenuItem value="Invoice">Invoice</MenuItem>
              </Select>
            </FormControl>

            <TextField
              type="date"
              label="From"
              size="small"
              value={startDate}
              onChange={handleStartDateChange}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              type="date"
              label="To"
              size="small"
              value={endDate}
              onChange={handleEndDateChange}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        )}

        {payments.length === 0 && !loading ? (
          syncStatus === 'pending_sync' || syncStatus === null ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress size={24} sx={{ mb: 1 }} />
              <Typography color="text.secondary">
                Setting up billing... This may take a moment.
              </Typography>
            </Box>
          ) : syncStatus === 'sync_error' ? (
            <Alert severity="warning" sx={{ my: 2 }}>
              There was an issue setting up your billing. Please refresh the
              page or contact support.
            </Alert>
          ) : (
            <Typography
              color="text.secondary"
              sx={{ py: 4, textAlign: 'center' }}
            >
              No payment history found
            </Typography>
          )
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Platform</TableCell>
                    <TableCell>Reference</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="center">Invoice</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {payments.map((payment) => {
                    const typeColors = getTypeChipColor(payment.type)
                    const platformColors = getPlatformChipColor(
                      payment.platform,
                    )

                    return (
                      <TableRow key={payment.id} hover>
                        <TableCell>{formatDate(payment.created_at)}</TableCell>
                        <TableCell>
                          <Chip
                            label={
                              payment.type === 'Charge'
                                ? 'Payment Requested'
                                : payment.type === 'Payment'
                                  ? 'Payment Received'
                                  : payment.type === 'CreditNote' &&
                                    'Credit Note'
                            }
                            size="small"
                            sx={{
                              backgroundColor: typeColors.bg,
                              color: typeColors.color,
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={payment.platform}
                            size="small"
                            sx={{
                              backgroundColor: platformColors.bg,
                              color: platformColors.color,
                              fontWeight: 500,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace' }}
                          >
                            {payment.external_reference || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: payment.amount < 0 ? '#388E3C' : 'inherit',
                            }}
                          >
                            {formatAmount(payment.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {payment.type === 'Charge' &&
                          payment.external_reference ? (
                            <IconButton
                              size="small"
                              onClick={() => handleDownload(payment.id)}
                              disabled={downloading === payment.id}
                              title="Download invoice"
                            >
                              {downloading === payment.id ? (
                                <CircularProgress size={18} />
                              ) : (
                                <DownloadIcon fontSize="small" />
                              )}
                            </IconButton>
                          ) : (
                            <Typography variant="body2" color="text.disabled">
                              -
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </CardContent>
    </Card>
  )
}
